import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useOpenAppNavigation } from "@/hooks/useOpenAppNavigation";
import { SortingState } from "@tanstack/react-table";
import useWorkspace from "@/hooks/useWorkspace";
import useUsers from "@/hooks/useUsers";
import { User } from "@/models/users/User";
import Workspace from "@/models/workspace/Workspace";
import { debounce } from "lodash";
import useTenant from "@/hooks/useTenant";
import Tenant, { ParentTenantInformation } from "@/models/tenant/tenant";
import { usePersistedWorkspaceSorting } from "./usePersistedWorkspaceSorting";
import { readWorkspaceSearchParams } from "@/lib/navigation/workspaceSearchParams";
import { backToWorkspaceSearchState } from "@/lib/navigation/workspaceReturn";

interface UseWorkspaceTableDataReturn {
  data: Workspace[];
  users: User[];
  search: string;
  /** The search the table is actually filtered by — `search` after the debounce. */
  debouncedSearch: string;
  /** Workspaces matching the search across all pages; undefined until the first page lands. */
  totalRecords: number | undefined;
  pageCount: number;
  pageIndex: number;
  pageSize: number;
  sorting: SortingState;
  setPageIndex: (pageIndex: number) => void;
  setPageSize: (pageSize: number) => void;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
  handleRowClick: (rowIndex: number, event?: React.MouseEvent) => void;
  handleSearchChange: (searchValue: string) => void;
  isLoading: boolean;
  parentTenant: ParentTenantInformation | null;
  availableSubtenants: Tenant[];
  currentTenant: Tenant | null;
}

const useWorkspaceTableData = (): UseWorkspaceTableDataReturn => {
  // Hooks
  const { useQueryPagination: workspacePagination } = useWorkspace();
  const { useQueryAllTenantUsers } = useUsers();
  const {
    useQueryGetParentTenant,
    useQueryCurrent,
    useQueryGetAvailableSubtenants,
  } = useTenant();
  const { openNavigation } = useOpenAppNavigation();
  // Data state
  const [data, setData] = useState<Workspace[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [parentTenant, setParentTenant] =
    useState<ParentTenantInformation | null>(null);
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [availableSubtenants, setAvailableSubtenants] = useState<Tenant[]>([]);
  const [pageCount, setPageCount] = useState(0);
  // Pagination state
  const [pageIndex, setPageIndex] = useState(0); // zero-based index
  const [pageSize, setPageSize] = useState(20);

  // Sorting state — the user's choice is remembered between visits (LAR-132)
  const [sorting, setSorting] = usePersistedWorkspaceSorting();

  // Search state. A search kept in the address (LAR-235: "Tilbage" from a result, the
  // browser's back button, a reload) is where the page resumes; a plain visit starts empty.
  const location = useLocation();
  const [input, setInput] = useState(
    () => readWorkspaceSearchParams(location.search).search
  ); // For capturing immediate user input
  const [search, setSearch] = useState(input); // For the actual debounced search

  // Abort controller state
  const [abortController, setAbortController] =
    useState<AbortController | null>(null);

  // Fetch data
  const fetchedWorkspaces = workspacePagination(
    {
      pageNumber: pageIndex + 1, // Convert zero-based index to one-based page number
      pageSize,
      sortAscending: sorting.length ? !sorting[0].desc : true,
      sortBy: sorting.length ? sorting[0].id : "",
      search,
    },
    abortController?.signal
  );

  // Check if any workspace has sharedWithSubTenants and if the current user is a sub-tenant
  // Parent tenant users don't need parent tenant info (they ARE the parent)
  // Sub-tenant users need parent tenant info to show parent branding
  const shouldFetchParentTenant = useMemo(() => {
    return data.some(
      (workspace) =>
        workspace.sharedWithSubTenants &&
        workspace.sharedWithSubTenants.length > 0 &&
        currentTenant &&
        workspace.sharedWithSubTenants.includes(currentTenant.id)
    );
  }, [data, currentTenant]);

  const fetchedCurrentTenant = useQueryCurrent();
  const fetchedParentTenant = useQueryGetParentTenant(shouldFetchParentTenant);
  const fetchedUsers = useQueryAllTenantUsers();
  const fetchedAvailableSubtenants = useQueryGetAvailableSubtenants();

  // Effects
  useEffect(() => {
    if (
      fetchedWorkspaces.isFetched &&
      fetchedWorkspaces.data &&
      fetchedWorkspaces.data.data
    ) {
      setData(fetchedWorkspaces.data.data);
      setPageCount(fetchedWorkspaces.data.totalPages);
    }
  }, [fetchedWorkspaces.data, fetchedWorkspaces.isFetched]);

  useEffect(() => {
    if (fetchedUsers.isFetched && fetchedUsers.data && fetchedUsers.data) {
      setUsers(fetchedUsers.data);
    }
  }, [fetchedUsers.data, fetchedUsers.isFetched]);

  useEffect(() => {
    if (fetchedParentTenant.isFetched && fetchedParentTenant.data) {
      setParentTenant(fetchedParentTenant.data);
    }
  }, [fetchedParentTenant.data, fetchedParentTenant.isFetched]);

  useEffect(() => {
    if (fetchedCurrentTenant.isFetched && fetchedCurrentTenant.data) {
      setCurrentTenant(fetchedCurrentTenant.data);
    }
  }, [fetchedCurrentTenant.data, fetchedCurrentTenant.isFetched]);

  useEffect(() => {
    if (
      fetchedAvailableSubtenants.isFetched &&
      fetchedAvailableSubtenants.data
    ) {
      setAvailableSubtenants(fetchedAvailableSubtenants.data);
    }
  }, [fetchedAvailableSubtenants.data, fetchedAvailableSubtenants.isFetched]);

  // Handlers
  const handleRowClick = (rowIndex: number, event?: React.MouseEvent) => {
    const id = data[rowIndex].id;
    // Opened from a search, the workspace gets a way back to it, as "Åbn workspace"
    // gives it; the plain list needs none, the breadcrumb leads there.
    const navigateOptions = search.trim()
      ? {
          state: backToWorkspaceSearchState(
            `${location.pathname}${location.search}`
          ),
        }
      : undefined;
    openNavigation({ kind: "workspaceFiles", workspaceId: id }, event, {
      navigateOptions,
    });
  };

  useEffect(() => {
    const debouncedFetch = debounce((searchValue: string) => {
      if (abortController) {
        abortController.abort();
      }

      const controller = new AbortController();
      setAbortController(controller);

      setPageIndex(0);
      setSearch(searchValue);
    }, 300);

    debouncedFetch(input);

    return () => {
      debouncedFetch.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  const handleSearchChange = (searchValue: string) => {
    setInput(searchValue);
  };

  return {
    data: useMemo(() => data, [data]),
    users,
    search: input,
    debouncedSearch: search,
    totalRecords:
      fetchedWorkspaces.data?.totalRecords ??
      fetchedWorkspaces.data?.totalCount,
    pageCount,
    pageIndex,
    pageSize,
    sorting,
    setPageIndex,
    setPageSize,
    setSorting,
    handleRowClick,
    handleSearchChange,
    isLoading:
      fetchedWorkspaces.isPending ||
      fetchedUsers.isPending ||
      fetchedCurrentTenant.isPending ||
      (shouldFetchParentTenant && fetchedParentTenant.isPending),
    parentTenant,
    availableSubtenants,
    currentTenant,
  };
};

export default useWorkspaceTableData;
